import type { JSX } from 'react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Input } from '@artosera/ui';
import type {
  CiblesPivot,
  ErreurRegle,
  Regles,
  Surcharges,
} from '../../../../lib/correspondance.js';
import { dictionnaireEnTexte, texteEnDictionnaire } from '../../../../lib/reglesEdition.js';
import type { SourceLot } from '../../../../lib/reprise.js';
import reprise from '../Reprise.module.css';
import styles from './Correspondance.module.css';

/** Les champs que les règles alimentent, sous la forme `nature.champ`. */
function champsAlimentes(regles: Regles, cibles: CiblesPivot | undefined) {
  return Object.entries(regles.natures).flatMap(([nature, bloc]) =>
    Object.keys(bloc.colonnes).map((cle) => ({
      cle: `${nature}.${cle}`,
      entete:
        cibles?.natures
          .find((cible) => cible.nature === nature)
          ?.colonnes.find((colonne) => colonne.cle === cle)?.entete ?? cle,
      nature,
    })),
  );
}

/**
 * Les choix propres à la galerie, à part du profil partagé : le fichier de
 * chaque rôle, des dictionnaires de valeurs, des constantes. Le profil reste
 * le même pour toutes les galeries ; ces réglages ne s'appliquent qu'à ce lot,
 * et c'est ce que le cadre et son titre disent.
 */
export function SurchargesEditeur({
  regles,
  cibles,
  surcharges,
  onChange,
  sources,
  erreurs,
  peutEnregistrer,
  raisonBloquee,
  enregistrement,
  onEnregistrer,
  modifie,
}: {
  regles: Regles;
  cibles: CiblesPivot | undefined;
  surcharges: Surcharges;
  onChange: (suite: Surcharges) => void;
  sources: readonly SourceLot[];
  erreurs: readonly ErreurRegle[];
  peutEnregistrer: boolean;
  /** Pourquoi on ne peut pas enregistrer (pas de profil), sinon `null`. */
  raisonBloquee: string | null;
  enregistrement: boolean;
  onEnregistrer: () => void;
  modifie: boolean;
}): JSX.Element {
  const { t } = useTranslation('console');
  const id = useId();
  const champs = champsAlimentes(regles, cibles);
  const libreDictionnaire = champs.find((champ) => !(champ.cle in surcharges.dictionnaires));
  const libreConstante = champs.find((champ) => !(champ.cle in surcharges.constantes));
  const roles = regles.sources.map((source) => source.role);

  const renommer = <T,>(enregistrement: Record<string, T>, ancienne: string, nouvelle: string) =>
    Object.fromEntries(
      Object.entries(enregistrement).map(([cle, valeur]) => [
        cle === ancienne ? nouvelle : cle,
        valeur,
      ]),
    );

  return (
    <section className={styles.surcharges} aria-labelledby={`${id}-titre`}>
      <div className={reprise.rangee}>
        <h3 id={`${id}-titre`} className={reprise.sousTitre}>
          {t('correspondance.surcharges.titre')}
        </h3>
        <Badge tone="reserved">{t('correspondance.surcharges.portee')}</Badge>
      </div>
      <p className={reprise.texte}>{t('correspondance.surcharges.texte')}</p>

      {erreurs.length > 0 ? (
        <ul className={styles.erreursChamp} role="alert">
          {erreurs.map((erreur) => (
            <li key={`${erreur.chemin}-${erreur.message}`}>{erreur.message}</li>
          ))}
        </ul>
      ) : null}

      {roles.length > 1 ? (
        <div className={reprise.pile}>
          <h4 className={reprise.sousTitre}>{t('correspondance.surcharges.fichiers')}</h4>
          <p className={reprise.discret}>{t('correspondance.surcharges.fichiersAide')}</p>
          {roles.map((role, rang) => (
            <div key={role} className={styles.parametre}>
              <label htmlFor={`${id}-fichier-${role}`}>
                {t('correspondance.surcharges.fichierDuRole', { role })}
              </label>
              <select
                id={`${id}-fichier-${role}`}
                className={styles.selection}
                value={surcharges.fichiers[role] ?? ''}
                onChange={(e) => {
                  const fichiers = { ...surcharges.fichiers };
                  if (e.target.value === '') delete fichiers[role];
                  else fichiers[role] = e.target.value;
                  onChange({ ...surcharges, fichiers });
                }}
              >
                <option value="">
                  {t('correspondance.surcharges.parOrdre', { rang: rang + 1 })}
                </option>
                {sources.map((source) => (
                  <option key={source.fichierId ?? source.nom} value={source.fichierId ?? ''}>
                    {source.nom}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>
      ) : null}

      <div className={reprise.pile}>
        <h4 className={reprise.sousTitre}>{t('correspondance.surcharges.dictionnaires')}</h4>
        <p className={reprise.discret}>{t('correspondance.surcharges.dictionnairesAide')}</p>
        {Object.entries(surcharges.dictionnaires).map(([cle, dictionnaire]) => (
          <div key={cle} className={styles.ligneRegle}>
            <div className={styles.parametre}>
              <label htmlFor={`${id}-dc-${cle}`}>{t('correspondance.surcharges.champ')}</label>
              <select
                id={`${id}-dc-${cle}`}
                className={styles.selection}
                value={cle}
                onChange={(e) =>
                  onChange({
                    ...surcharges,
                    dictionnaires: renommer(surcharges.dictionnaires, cle, e.target.value),
                  })
                }
              >
                {champs
                  .filter((champ) => champ.cle === cle || !(champ.cle in surcharges.dictionnaires))
                  .map((champ) => (
                    <option key={champ.cle} value={champ.cle}>
                      {`${t(`reprise.natures.${champ.nature}`)} · ${champ.entete}`}
                    </option>
                  ))}
              </select>
            </div>
            <div className={`${styles.parametre} ${styles.parametreLarge}`}>
              <label htmlFor={`${id}-dv-${cle}`}>{t('correspondance.editeur.dictionnaire')}</label>
              <textarea
                id={`${id}-dv-${cle}`}
                className={styles.zoneTexte}
                rows={3}
                spellCheck={false}
                defaultValue={dictionnaireEnTexte(dictionnaire.valeurs)}
                onChange={(e) =>
                  onChange({
                    ...surcharges,
                    dictionnaires: {
                      ...surcharges.dictionnaires,
                      [cle]: { ...dictionnaire, valeurs: texteEnDictionnaire(e.target.value) },
                    },
                  })
                }
              />
              <span className={styles.aideChamp}>
                {t('correspondance.editeur.dictionnaireAide')}
              </span>
            </div>
            <div className={styles.parametre}>
              <label htmlFor={`${id}-dd-${cle}`}>
                {t('correspondance.editeur.valeurParDefaut')}
              </label>
              <Input
                id={`${id}-dd-${cle}`}
                value={dictionnaire.defaut ?? ''}
                onChange={(e) => {
                  const { defaut: _ancien, ...reste } = dictionnaire;
                  onChange({
                    ...surcharges,
                    dictionnaires: {
                      ...surcharges.dictionnaires,
                      [cle]: e.target.value === '' ? reste : { ...reste, defaut: e.target.value },
                    },
                  });
                }}
              />
            </div>
            <Button
              variant="ghost"
              size="sm"
              aria-label={t('correspondance.surcharges.retirerDictionnaire')}
              onClick={() => {
                const dictionnaires = { ...surcharges.dictionnaires };
                delete dictionnaires[cle];
                onChange({ ...surcharges, dictionnaires });
              }}
            >
              {t('correspondance.editeur.retirer')}
            </Button>
          </div>
        ))}
        <div className={reprise.actions}>
          <Button
            variant="secondary"
            size="sm"
            disabled={!libreDictionnaire}
            onClick={() =>
              libreDictionnaire &&
              onChange({
                ...surcharges,
                dictionnaires: {
                  ...surcharges.dictionnaires,
                  [libreDictionnaire.cle]: { valeurs: {} },
                },
              })
            }
          >
            {t('correspondance.surcharges.ajouterDictionnaire')}
          </Button>
        </div>
      </div>

      <div className={reprise.pile}>
        <h4 className={reprise.sousTitre}>{t('correspondance.surcharges.constantes')}</h4>
        <p className={reprise.discret}>{t('correspondance.surcharges.constantesAide')}</p>
        {Object.entries(surcharges.constantes).map(([cle, valeur]) => (
          <div key={cle} className={styles.ligneRegle}>
            <div className={styles.parametre}>
              <label htmlFor={`${id}-cc-${cle}`}>{t('correspondance.surcharges.champ')}</label>
              <select
                id={`${id}-cc-${cle}`}
                className={styles.selection}
                value={cle}
                onChange={(e) =>
                  onChange({
                    ...surcharges,
                    constantes: renommer(surcharges.constantes, cle, e.target.value),
                  })
                }
              >
                {champs
                  .filter((champ) => champ.cle === cle || !(champ.cle in surcharges.constantes))
                  .map((champ) => (
                    <option key={champ.cle} value={champ.cle}>
                      {`${t(`reprise.natures.${champ.nature}`)} · ${champ.entete}`}
                    </option>
                  ))}
              </select>
            </div>
            <div className={styles.parametre}>
              <label htmlFor={`${id}-cv-${cle}`}>{t('correspondance.editeur.valeur')}</label>
              <Input
                id={`${id}-cv-${cle}`}
                value={valeur}
                onChange={(e) =>
                  onChange({
                    ...surcharges,
                    constantes: { ...surcharges.constantes, [cle]: e.target.value },
                  })
                }
              />
            </div>
            <Button
              variant="ghost"
              size="sm"
              aria-label={t('correspondance.surcharges.retirerConstante')}
              onClick={() => {
                const constantes = { ...surcharges.constantes };
                delete constantes[cle];
                onChange({ ...surcharges, constantes });
              }}
            >
              {t('correspondance.editeur.retirer')}
            </Button>
          </div>
        ))}
        <div className={reprise.actions}>
          <Button
            variant="secondary"
            size="sm"
            disabled={!libreConstante}
            onClick={() =>
              libreConstante &&
              onChange({
                ...surcharges,
                constantes: { ...surcharges.constantes, [libreConstante.cle]: '' },
              })
            }
          >
            {t('correspondance.surcharges.ajouterConstante')}
          </Button>
        </div>
      </div>

      {peutEnregistrer ? (
        <div className={reprise.pile}>
          <div className={reprise.actions}>
            <Button
              loading={enregistrement}
              disabled={raisonBloquee !== null || !modifie}
              onClick={onEnregistrer}
            >
              {t('correspondance.surcharges.enregistrer')}
            </Button>
          </div>
          {raisonBloquee ? <p className={reprise.discret}>{raisonBloquee}</p> : null}
        </div>
      ) : null}
    </section>
  );
}
